import { isRejectedWithValue } from "@reduxjs/toolkit"
import { t } from "i18next"

import type { LocalizedText } from "@/shared/i18n"

import type { Middleware } from "@reduxjs/toolkit"

interface RtkQueryPayloadData {
  ok?: boolean
  message?: string
  error?: Array<{ message: string; code?: string }> | string
  originalError?: string
  isGenericError?: boolean
}

interface RtkQueryPayload {
  status: number | string
  data?: unknown
  error?: string
}

const HTTP_STATUS_MESSAGES = {
  400: { key: "error-bad-request", fallback: "Invalid request" },
  401: { key: "error-unauthorized", fallback: "Session expired. Please reload and try again" },
  402: { key: "error-insufficient-balance", fallback: "Insufficient balance" },
  403: { key: "error-forbidden", fallback: "You don't have permission for this action" },
  404: { key: "error-not-found", fallback: "Resource not found" },
  408: { key: "error-request-timeout", fallback: "Request took too long. Please try again" },
  409: { key: "error-conflict", fallback: "This action conflicts with current state" },
  422: { key: "error-validation", fallback: "Invalid data provided" },
  429: { key: "error-rate-limit", fallback: "Too many requests. Please wait and try again" },
  500: { key: "error-server", fallback: "An unexpected error occurred. Please try again" },
  502: { key: "error-gateway", fallback: "Service temporarily unavailable" },
  503: { key: "error-service-unavailable", fallback: "Service is temporarily unavailable. Please try again shortly" },
  504: { key: "error-gateway-timeout", fallback: "Server took too long to respond. Please try again" }
} as const

const NON_HTTP_MESSAGES = {
  FETCH_ERROR: { key: "error-network", fallback: "Connection error. Check your internet" },
  TIMEOUT_ERROR: { key: "error-timeout", fallback: "Request timed out. Please try again" },
  PARSING_ERROR: { key: "error-parsing", fallback: "Unexpected response from server" }
} as const

const ERROR_CODE_MESSAGES = {
  insufficient_purchased_balance: {
    key: "error-insufficient-balance",
    fallback: "Insufficient balance"
  },
  cannot_report_admin: {
    key: "error-cannot-report-admin",
    fallback: "You cannot report administrators."
  },
  business_connection_not_found: {
    key: "error-business-connection-not-found",
    fallback: "Connection not found"
  },
  local_settings_write_redirect: {
    key: "error-local-settings-write-redirect",
    fallback: "This setting is managed by grid “{{grid}}”. Edit it in the grid or turn off sync for this chat."
  },
  local_settings_write_blocked: {
    key: "error-local-settings-write-blocked",
    fallback:
      "This setting is managed by grid “{{grid}}”. Ask a grid administrator to change it or turn off sync for this chat."
  },
  settings_copy_grid_conflict: {
    key: "error-settings-copy-grid-conflict",
    fallback:
      "Copying was blocked by grid settings. Some selected chats use grid settings. Deselect those chats or turn off sync for the settings you want to copy."
  },
  spam_settings_update_in_progress: {
    key: "error-spam-settings-update-in-progress",
    fallback: "Antispam settings are temporarily read-only during an update. Try again in a few minutes."
  },
  retired_command: {
    key: "error-retired-command",
    fallback: "This command is retired and cannot be configured"
  }
} as const satisfies Record<string, LocalizedText>

type ApiErrorCode = keyof typeof ERROR_CODE_MESSAGES

const MESSAGE_PATTERNS = [
  {
    pattern: /bot.*not.*admin|not enough rights|have no rights/i,
    key: "error-bot-no-admin",
    fallback: "Bot has no admin rights in this group. Grant admin rights to the bot"
  },
  {
    pattern: /bot.*blocked.*user|user.*blocked.*bot|bot was blocked/i,
    key: "error-bot-blocked",
    fallback: "Bot is blocked by the user"
  },
  {
    pattern: /bot.*not.*member|chat not found|bot was kicked/i,
    key: "error-bot-not-in-group",
    fallback: "Bot is not added to this group. Add the bot to the group"
  }
] as const

const UNKNOWN_ERROR_FALLBACK = "An unexpected error occurred"

const isPayloadDataObject = (data: unknown): data is RtkQueryPayloadData =>
  Object.prototype.toString.call(data) === "[object Object]"

const isErrorMessageObject = (error: unknown): error is { message: string } =>
  typeof error === "object" && error !== null && "message" in error && typeof error.message === "string"

const isErrorCodeObject = (error: unknown): error is { code: string } =>
  typeof error === "object" && error !== null && "code" in error && typeof error.code === "string"

const isRtkQueryPayload = (payload: unknown): payload is RtkQueryPayload => {
  if (typeof payload !== "object" || payload === null || !("status" in payload)) return false
  return typeof payload.status === "number" || typeof payload.status === "string"
}

const isHttpStatus = (status: number): status is keyof typeof HTTP_STATUS_MESSAGES => status in HTTP_STATUS_MESSAGES

const isNonHttpStatus = (status: string): status is keyof typeof NON_HTTP_MESSAGES => status in NON_HTTP_MESSAGES

const resolveByMessagePattern = (message: string): string | null => {
  for (const { pattern, key, fallback } of MESSAGE_PATTERNS) {
    if (pattern.test(message)) {
      return t(`common:${key}`, fallback)
    }
  }
  return null
}

const extractOriginalMessage = (payload: RtkQueryPayload): string | undefined => {
  if (isPayloadDataObject(payload.data)) {
    if (Array.isArray(payload.data.error) && isErrorMessageObject(payload.data.error[0])) {
      return payload.data.error[0].message
    }
    if (typeof payload.data.error === "string") {
      return payload.data.error
    }
    if (typeof payload.data.message === "string") {
      return payload.data.message
    }
  }
  if (typeof payload.error === "string") {
    return payload.error
  }
  return undefined
}

const extractErrorCode = (payload: RtkQueryPayload): string | undefined => {
  if (!isPayloadDataObject(payload.data) || !Array.isArray(payload.data.error)) return undefined

  const firstError = payload.data.error[0]
  if (!isErrorCodeObject(firstError)) return undefined

  return firstError.code
}

interface ResolvedMessage {
  message: string
  isGeneric: boolean
}

const isKnownErrorCode = (code: string): code is ApiErrorCode => Object.hasOwn(ERROR_CODE_MESSAGES, code)

const resolveByErrorCode = (code: string, payload: RtkQueryPayload): ResolvedMessage | null => {
  if (!isKnownErrorCode(code)) return null

  const entry = ERROR_CODE_MESSAGES[code]

  if (code === "local_settings_write_redirect" || code === "local_settings_write_blocked") {
    const firstError: unknown =
      isPayloadDataObject(payload.data) && Array.isArray(payload.data.error) ? payload.data.error[0] : undefined
    const grid =
      typeof firstError === "object" && firstError !== null
        ? "grid_name" in firstError && typeof firstError.grid_name === "string" && firstError.grid_name.trim()
          ? firstError.grid_name
          : "grid_id" in firstError && typeof firstError.grid_id === "string"
            ? firstError.grid_id
            : t("common:error-settings-grid-unknown", "Settings Grid")
        : t("common:error-settings-grid-unknown", "Settings Grid")
    return { message: t(`common:${entry.key}`, entry.fallback, { grid }), isGeneric: false }
  }
  return { message: t(`common:${entry.key}`, entry.fallback), isGeneric: false }
}

const resolveMessage = (status: number | string): ResolvedMessage => {
  if (typeof status === "number") {
    if (!isHttpStatus(status)) return { message: t("common:error-unknown", UNKNOWN_ERROR_FALLBACK), isGeneric: true }
    const { key, fallback } = HTTP_STATUS_MESSAGES[status]
    return { message: t(`common:${key}`, fallback), isGeneric: false }
  }

  if (!isNonHttpStatus(status)) return { message: t("common:error-unknown", UNKNOWN_ERROR_FALLBACK), isGeneric: true }
  const { key, fallback } = NON_HTTP_MESSAGES[status]
  return { message: t(`common:${key}`, fallback), isGeneric: false }
}

/** Localized text for an error the API identifies by a known code or a recognized Telegram failure, otherwise null.
 * `.unwrap()` hands callers the payload as the API sent it, so toasts resolve it here as well. */
export const resolveRecognizedErrorMessage = (error: unknown): string | null => {
  if (!isRtkQueryPayload(error) || error.status === "CUSTOM_ERROR") return null

  const errorCode = extractErrorCode(error)
  const codeMatch = errorCode ? resolveByErrorCode(errorCode, error) : null
  if (codeMatch) return codeMatch.message

  const originalMessage = extractOriginalMessage(error)
  return originalMessage ? resolveByMessagePattern(originalMessage) : null
}

/** Resolve API diagnostics through stable codes and transport status only. */
export const resolveApiErrorMessage = (error: unknown): string => {
  if (!isRtkQueryPayload(error)) return t("common:error-unknown", UNKNOWN_ERROR_FALLBACK)
  const code = extractErrorCode(error)
  const recognized = code ? resolveByErrorCode(code, error) : null
  if (recognized) return recognized.message
  const status =
    error.status === "PARSING_ERROR" && "originalStatus" in error && typeof error.originalStatus === "number"
      ? error.originalStatus
      : error.status
  return resolveMessage(status).message
}

export const rtkErrorMiddleware: Middleware = () => next => action => {
  if (!isRejectedWithValue(action)) return next(action)
  if (!isRtkQueryPayload(action.payload)) return next(action)

  const payload = action.payload

  // Skip custom endpoint errors so we don't overwrite queryFn/transformErrorResponse messaging.
  if (payload.status === "CUSTOM_ERROR") return next(action)

  const originalMessage = extractOriginalMessage(payload)
  const recognizedMessage = resolveRecognizedErrorMessage(payload)
  const resolved = recognizedMessage ? { message: recognizedMessage, isGeneric: false } : resolveMessage(payload.status)

  const { message: mappedMessage, isGeneric } = resolved

  let newPayload: RtkQueryPayload

  if (isPayloadDataObject(payload.data)) {
    const mappedData =
      Array.isArray(payload.data.error) && isErrorMessageObject(payload.data.error[0])
        ? { error: [{ ...payload.data.error[0], message: mappedMessage }, ...payload.data.error.slice(1)] }
        : typeof payload.data.error === "string"
          ? { error: mappedMessage }
          : typeof payload.data.message === "string"
            ? { message: mappedMessage }
            : { error: [{ message: mappedMessage }] }

    newPayload = {
      ...payload,
      data: {
        ...payload.data,
        ...mappedData,
        isGenericError: isGeneric,
        ...(originalMessage ? { originalError: originalMessage } : {})
      }
    }
  } else if (typeof payload.error === "string") {
    newPayload = { ...payload, error: mappedMessage, data: { isGenericError: isGeneric, originalError: payload.error } }
  } else {
    return next(action)
  }

  return next({ ...action, payload: newPayload })
}
